import { createPaginator } from '@orange-marmalade/paginate-this';
import { fromJS, Record, List } from 'immutable';
import { createDuck } from 'redux-duck';
import PaginateThisDuckCreator from '../middleware/paginate-this';
import { api, cleanQuery, formatError } from '../middleware/client';

const BASE_URL = `${process.env.REACT_APP_REST_URI}/community`;
const locator = ({ CommunitySearch }) => CommunitySearch.get('list');
const listId = 'community';
const defaultQuery = {
  page: 1,
  pageSize: 15,
  sort: 'name',
  sortReverse: false
};
const sortFields = [
  {
    id: 'name',
    name: 'Name: A-Z',
    order: 'ASC'
  },
  {
    id: 'name',
    name: 'Name: Z-A',
    order: 'DESC'
  }
];

const PhotoRecord = Record({
  id: undefined,
  poiId: null,
  filename: '',
  order: null
});

const CommunityRecord = Record({
  id: undefined,
  developmentId: null,
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  description: '',
  isGated: null,
  isProofed: null,
  latitude: '',
  longitude: null,
  lastUpdated: undefined,
  photo: '',
  photos: new List()
});

const paginateReducer = createPaginator({
  listId,
  fetch,
  locator,
  cache: true,
  initialSettings: {
    // initialized is true to prevent extra actions being fired
    // since we actually initialize the list using query parameters
    initialized: true,
    ...defaultQuery
  }
});

const duck = createDuck('POISearch', 'nabor');
const LOAD_TYPE = duck.defineType('LOAD');
const LOAD_SUCCESS_TYPE = duck.defineType('LOAD_SUCCESS');
const LOAD_FAIL_TYPE = duck.defineType('LOAD_FAIL');
const ABORTED_REQUEST_TYPE = duck.defineType('ABORTED_REQUEST');
const ABORTED_REQUEST = duck.createAction(ABORTED_REQUEST_TYPE);

// custom abort support
var lastRequest;

const initialState = fromJS({
  list: paginateReducer(),
  detail: {
    loaded: false,
    loading: false,
    record: new CommunityRecord()
  }
});

function reducer(state = initialState, action) {
  if (action.type === LOAD_TYPE) {
    state = state.mergeIn(['detail'], {
      loaded: action.id,
      loading: true,
      error: null
    });
  } else if (action.type === LOAD_SUCCESS_TYPE) {
    state = state.mergeIn(['detail'], {
      loaded: action.id,
      loading: false,
      record: fromJS(action.payload || {}, function (key, value, path) {
        if (key === '') {
          return new CommunityRecord(value);
        } else if (key === 'photos') {
          return value.map(function (photo) {
            return new PhotoRecord(photo);
          });
        } else {
          return value;
        }
      })
    });
  } else if (action.type === LOAD_FAIL_TYPE) {
    state = state.mergeIn(['detail'], {
      loading: false,
      error: formatError(action.error)
    });
  } else if (action.type === ABORTED_REQUEST_TYPE) {
    state = state.setIn(['list', 'isLoading'], false);
  } else {
    state = state.setIn(['list'], paginateReducer.call(this, state.get('list'), action));
  }
  return state;
}

function abortRequest() {
  if (lastRequest && lastRequest.abort) {
    lastRequest.abort();
  }

  return ABORTED_REQUEST(lastRequest);
}

function fetch(params) {
  const { id, query } = params;

  if (id) {
    let url = `${BASE_URL}/${id}`;
    return {
      types: [LOAD_TYPE, LOAD_SUCCESS_TYPE, LOAD_FAIL_TYPE],
      promise: client =>
        client.get(url).then(function (response) {
          return response;
        }),
      id
    };
  } else {
    lastRequest = api.get(BASE_URL, {
      query: cleanQuery({
        ...query,
        page: undefined,
        pageSize: undefined,
        offset: query.pageSize * (query.page - 1),
        limit: query.pageSize
      })
    });

    return lastRequest
      .then(function (response) {
        return {
          data: {
            results: response.list,
            total_count: response.total
          }
        };
      })
      .catch(error => {
        throw formatError(error);
      });
  }
}

const paginateThis = new PaginateThisDuckCreator({
  listId,
  locator,
  defaultQuery,
  abortRequest,
  // Lists filters that will automatically get parsed from the query string
  availableFilters: {
    name: undefined
  }
});

export { paginateThis, fetch, sortFields, CommunityRecord };
export default reducer;



// WEBPACK FOOTER //
// ./src/ducks/CommunitySearch.js